import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { Row } from 'write-excel-file';

import type { WidgetExportOptions } from 'src/components/dashboard/DashboardPage/components/WidgetFrame/useWidgetExport';
import { getRelativeValue } from 'src/components/dashboard/DashboardPage/helper';

import type { QuestionBarsWithLabel } from './helper';

type UseExportOptionsProps = {
  data: QuestionBarsWithLabel | null;
  comparisonData: QuestionBarsWithLabel | null;
} & Pick<WidgetExportOptions, 'widgetName' | 'queryFilters'>;

const useMapRows = () => {
  const { t } = useTranslation('followUp');

  return useCallback(
    (questions: QuestionBarsWithLabel | null, widgetName: string): Row[] => {
      if (!questions) return [];

      return [
        [
          { value: widgetName, fontWeight: 'bold' },
          {
            value: t(
              'statistics.deepDiveQuestionsWidget.exportLabels.negativeN',
            ),
            fontWeight: 'bold',
          },
          {
            value: t(
              'statistics.deepDiveQuestionsWidget.exportLabels.negativeP',
            ),
            fontWeight: 'bold',
          },
          {
            value: t(
              'statistics.deepDiveQuestionsWidget.exportLabels.neutralN',
            ),
            fontWeight: 'bold',
          },
          {
            value: t(
              'statistics.deepDiveQuestionsWidget.exportLabels.neutralP',
            ),
            fontWeight: 'bold',
          },
          {
            value: t(
              'statistics.deepDiveQuestionsWidget.exportLabels.positiveN',
            ),
            fontWeight: 'bold',
          },
          {
            value: t(
              'statistics.deepDiveQuestionsWidget.exportLabels.positiveP',
            ),
            fontWeight: 'bold',
          },
        ],
        ...questions.map<Row>((questionBar) => {
          const { question, detractor, passive, promoter } = questionBar;
          const total = detractor + passive + promoter;

          return [
            {
              value: question,
            },
            {
              value: detractor,
            },
            {
              value: `${getRelativeValue(detractor, total, 0)}%`,
              align: 'right',
            },
            {
              value: passive,
            },
            {
              value: `${getRelativeValue(passive, total, 0)}%`,
              align: 'right',
            },
            {
              value: promoter,
            },
            {
              value: `${getRelativeValue(promoter, total, 0)}%`,
              align: 'right',
            },
          ];
        }),
      ];
    },
    [t],
  );
};

export const useExportOptions = ({
  data,
  comparisonData,
  widgetName,
  queryFilters,
}: UseExportOptionsProps): WidgetExportOptions => {
  const mapRows = useMapRows();

  return useMemo(() => {
    const rows = mapRows(data, widgetName);
    const comparisonRows = comparisonData
      ? mapRows(comparisonData, widgetName)
      : undefined;

    return {
      widgetName,
      rows,
      comparisonRows,
      queryFilters,
    };
  }, [mapRows, data, comparisonData, widgetName, queryFilters]);
};

export const useNpsQuestionBarSegmentColors = () => {
  const { colors } = useTheme();

  return useMemo(
    () => ({
      regular: {
        detractor: {
          backgroundColor: alpha(colors.error[600], 0.23),
          borderColor: colors.error[600],
          color: colors.error[900],
        },
        passive: {
          backgroundColor: colors.neutral[50],
          borderColor: colors.neutral[500],
          color: colors.neutral[800],
        },
        promoter: {
          backgroundColor: alpha(colors.tertiary[700], 0.1),
          borderColor: colors.tertiary[700],
          color: colors.tertiary[700],
        },
        notAnswered: {
          backgroundColor: alpha(colors.yellow[500], 0.1),
          borderColor: colors.yellow[400],
          color: colors.yellow[800],
        },
      },
      comparison: {
        detractor: {
          backgroundColor: colors.comparison[400],
          borderColor: colors.comparison[600],
          color: colors.white,
        },
        passive: {
          backgroundColor: colors.comparison[200],
          borderColor: colors.comparison[300],
          color: colors.comparison[600],
        },
        promoter: {
          backgroundColor: colors.comparison[50],
          borderColor: colors.comparison[100],
          color: colors.comparison[600],
        },
        notAnswered: {
          backgroundColor: colors.comparison[100],
          borderColor: colors.comparison[400],
          color: colors.comparison[600],
        },
      },
    }),
    [colors],
  );
};
